import React from 'react';
import { useField } from 'react-final-form';

import { Box, Stack, TextField } from '@mui/material';
import { DEFAULT_2_COLUMNS, DEFAULT_3_COLUMNS } from '@wp-block-builder/blocks/ColumnsSection/parsers';
import RadioTabsWrapper from '@wp-block-builder/components/inputs/RadioTabsWrapper';

import ColumnsCountToggle from './ColumnsCountToggle';
import ColumnsWidthSlider from './ColumnsWidthSlider';

function getValue(value: unknown, columnsCount: 2 | 3): [number] | [number, number] {
  if (!Array.isArray(value)) {
    return columnsCount === 3 ? DEFAULT_3_COLUMNS : DEFAULT_2_COLUMNS;
  }

  if (columnsCount === 2) {
    return typeof value[0] === 'number' ? [value[0]] : DEFAULT_2_COLUMNS;
  }
  if (typeof value[0] !== 'number' || typeof value[1] !== 'number') {
    return DEFAULT_3_COLUMNS;
  }
  return [value[0], value[1]];
}

const DIMENSIONS_RULE_OPTIONS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'manual', label: 'Fixed' },
];

function ManualWidthInput({ name, index }: { name: string; index: 0 | 1 | 2 }) {
  const { input: fixedWidthsInput } = useField(`${name}.props.fixedWidths`);
  const fixedWidthsValue = fixedWidthsInput.value || [null, null, null];
  const onChange: React.ChangeEventHandler<HTMLInputElement> = (ev) => {
    const stringValue = ev.target.value.replace(/[^\d]/g, '').trim();
    const value = [...fixedWidthsValue];
    value[index] = stringValue.length === 0 ? null : parseInt(stringValue);
    fixedWidthsInput.onChange(value);
  };
  return (
    <TextField
      size="small"
      value={fixedWidthsValue[index] || ''}
      onChange={onChange}
      placeholder="Auto"
      fullWidth
      helperText={<Box sx={{ textAlign: 'center' }}>Column {index + 1}</Box>}
      InputProps={{
        endAdornment: 'px',
      }}
    />
  );
}

type Props = {
  // Since this is a "special input", the name property points to the root block
  name: string;
};
export default function ColumnsLayoutInput({ name }: Props) {
  const { input: dimensionsRuleInput } = useField(`${name}.props.dimensionsRule`);
  const { input: columnsCountInput } = useField(`${name}.props.columnsCount`);
  const { input: columnGridDimensionsInput } = useField(`${name}.props.columnGridDimensions`);

  const onChangeColumnsCount = (val: unknown) => {
    if (val === 3) {
      columnsCountInput.onChange(3);
      columnGridDimensionsInput.onChange(DEFAULT_3_COLUMNS);
      return;
    }
    columnsCountInput.onChange(2);
    columnGridDimensionsInput.onChange(DEFAULT_2_COLUMNS);
  };

  const onChangeColumnGridDimensions = (val: number[]) => {
    columnGridDimensionsInput.onChange(val);
  };

  const dimensionsRuleValue = dimensionsRuleInput.value || 'percentage';
  const columnsCountValue = columnsCountInput.value === 2 ? 2 : 3;
  const columnGridDimensionsValue = getValue(columnGridDimensionsInput.value, columnsCountValue);

  const handleDimensionsRuleChange = (value: unknown) => {
    if (value === 'manual') {
      dimensionsRuleInput.onChange('manual');
      return;
    }
    dimensionsRuleInput.onChange('percentage');
  };

  const renderDimensionsRule = () => {
    if (dimensionsRuleValue === 'manual') {
      if (columnsCountValue === 3) {
        return (
          <Stack direction="row" spacing={1}>
            <ManualWidthInput name={name} index={0} />
            <ManualWidthInput name={name} index={1} />
            <ManualWidthInput name={name} index={2} />
          </Stack>
        );
      }
      return (
        <Stack direction="row" spacing={1}>
          <ManualWidthInput name={name} index={0} />
          <ManualWidthInput name={name} index={1} />
        </Stack>
      );
    }
    return (
      <ColumnsWidthSlider
        columnsCount={columnsCountValue}
        value={columnGridDimensionsValue}
        onChange={onChangeColumnGridDimensions}
      />
    );
  };
  return (
    <>
      <ColumnsCountToggle value={columnsCountValue} onChange={onChangeColumnsCount} />
      <RadioTabsWrapper
        label="Columns layout"
        options={DIMENSIONS_RULE_OPTIONS}
        onChange={handleDimensionsRuleChange}
        value={dimensionsRuleValue}
      >
        <Box py={2} px={1}>
          {renderDimensionsRule()}
        </Box>
      </RadioTabsWrapper>
    </>
  );
}
